export const DEFAULT_IDS_TO_PERSIST: string[] = ['ariaLiveWrapper'];
const DEFAULT_IDS_TO_AVOID_DISPLAY_NONE: string[] = ['MAIL_SURFACE'];

const isValidElement = (elementId: string, exclusions: string[]): boolean => {
    return elementId && exclusions.indexOf(elementId) === -1;
};

const canUseDisplayNone = (elementId: string) => {
    return DEFAULT_IDS_TO_AVOID_DISPLAY_NONE.indexOf(elementId) === -1;
};

const saveCurrentStateAndHideElement = (
    styleVisibilityMap: Map<
        string,
        {
            visibility: string;
            display: string;
        }
    >,
    ariaVisibilityMap: Map<string, boolean>,
    element: HTMLElement,
    hideVisibility: boolean = false
) => {
    const elementId: string = element.id;

    // Save current aria-hidden state and set it to true
    ariaVisibilityMap.set(elementId, element.getAttribute('aria-hidden') === 'true');
    element.setAttribute('aria-hidden', 'true');

    // If we have to hide the element, save current styling and hide it
    if (hideVisibility) {
        styleVisibilityMap.set(elementId, {
            visibility: element.style.visibility,
            display: element.style.display,
        });
        element.style.visibility = 'hidden';
        canUseDisplayNone(elementId) && (element.style.display = 'none');
    }
};

/**
 * Returns creates and relinquish focus trap methods for an element
 * @param id
 * @param elements
 * @param hideBackground
 * @param exclusions
 */
export const createFocusTrap = (
    id: string,
    hideBackground: boolean = false,
    exclusions: string[] = DEFAULT_IDS_TO_PERSIST
): (() => void) => {
    if (!document.getElementById(id)) {
        return () => {};
    }

    const styleVisibilityMap = new Map<
        string,
        {
            visibility: string;
            display: string;
        }
    >();
    const ariaVisibilityMap = new Map<string, boolean>();

    const elements: HTMLCollection = document.body.children;

    for (let i = 0, length = elements.length; i < length; i++) {
        const element = elements[i] as HTMLElement;
        const elementId: string = element?.id;
        if (isValidElement(elementId, exclusions)) {
            if (id === elementId) {
                element.removeAttribute('aria-hidden');
            } else {
                saveCurrentStateAndHideElement(
                    styleVisibilityMap,
                    ariaVisibilityMap,
                    element,
                    hideBackground
                );
            }
        }
    }

    const relinquishFocusTrap = () => {
        for (let i = 0, length = elements.length; i < length; i++) {
            const element = elements[i] as HTMLElement;
            const elementId = element?.id;

            if (isValidElement(elementId, exclusions)) {
                // Restore aria-hidden
                if (ariaVisibilityMap.has(elementId)) {
                    ariaVisibilityMap.get(elementId)
                        ? element.setAttribute('aria-hidden', 'true')
                        : element.removeAttribute('aria-hidden');
                }

                // Restore styling
                if (styleVisibilityMap.has(elementId) && hideBackground) {
                    element.style.visibility = styleVisibilityMap.get(elementId).visibility;
                    element.style.display = styleVisibilityMap.get(elementId).display;
                }
            }
        }
    };

    return relinquishFocusTrap;
};
